<template>
<div>
    <base-card :title="past ? $t('pastMedication') : $t('currentMedication')" overflowVisible @rightBtnClick="showAddSection = true" hideNeuCardClass :hideButtons="historyReadOnly">
        <template v-if="!showAddSection && !past" :slot="$uiHelpers.isMobile || $uiHelpers.isTablet ? 'append-btn-title' : 'right-button'">
            <add-button></add-button>
        </template>
        <div class="row mb-4" v-if="showAddSection">
            <div class="col-lg-8 col-12 p-1">
                <base-select @search="loadSelectOptions($event)" clearable searchable :loading="select.loading" :options="select.options" v-model="select.value" label="name" valueProp="id" :placeholder="$t('medication-ph')"></base-select>
            </div>
            <div class="col-lg-4 col-12 p-1 checkbox-med">
                <base-checkbox noMargin v-model="currentlyTakeMed" @input="() => medicationEndDate = null">{{$t('currentlyTakeMed')}}</base-checkbox>
            </div>
            <div class="col-lg-4 col-12 p-1">
                <base-datepicker v-model="medicationDate" dateBeforeNow resetButtton :placeholder="$t('startDate')" noMarginBottom></base-datepicker>
            </div>
            <div v-if="!currentlyTakeMed" class="col-lg-4 col-12 p-1">
                <base-datepicker v-model="medicationEndDate" resetButtton :placeholder="$t('endDate')" noMarginBottom></base-datepicker>
            </div>
            <div class="col-lg-2 col-12 p-1">
                <base-button @click="save">{{ $t('save-btn') }}</base-button>
            </div>
        </div>
        
        <paginated-table :key="tableKey" :fields="table.fields" :items="medicines" :empty-text="$t('nodrug')" :stacked="stacked">
            <template v-slot:cell(currentlyNoTakeMed)="data" v-if="!historyReadOnly">
                <base-checkbox v-if="showNoTakeMedSelector(data.item.dateEnd)" noMargin v-model="data.item.currentlyNoTakeMed" @input="() => stopMedication(data)">{{$t('currentlyNoTakeMed')}}</base-checkbox>
                <span v-else>{{$t('currentlyNoTakeMed')}}</span>
            </template>
            <template v-slot:cell(reminder)="data" v-if="!historyReadOnly">
                <b-icon-bell-fill :class="{'bell-reminder': true, blue: data.item.recurrenceReminderId}" @click.prevent="createMedicationReminder(data.item.code)"></b-icon-bell-fill>
            </template>
            <template v-slot:cell(removeReminder)="data" v-if="!historyReadOnly">
                <b-icon-bell-slash-fill v-if="data.item.recurrenceReminderId" @click.prevent="removeReminder(data.item.id)" class="bell-slash-reminder"></b-icon-bell-slash-fill>
            </template>
            <template v-slot:cell(remove)="data" v-if="!historyReadOnly">
                <base-icon class="clickable" src="/static-appopensalud/img/icons/morillas-svg-icons/primary/remove.svg" @click.prevent="remove(data)" :color="RED_COLOUR"></base-icon>
            </template>
            <template #cell(actionsMobile)="data" v-if="!historyReadOnly">
                <div class="mb-2 d-block">
                    <base-checkbox v-if="showNoTakeMedSelector(data.item.dateEnd)" noMargin v-model="data.item.currentlyNoTakeMed" @input="() => stopMedication(data)">{{$t('currentlyNoTakeMed')}}</base-checkbox>
                    <span v-else>{{$t('currentlyNoTakeMed')}}</span>
                </div>
                <div class="mb-2">
                    <b-icon-bell-fill class="" :class="{'bell-reminder': true, blue: data.item.recurrenceReminderId}" @click.prevent="createMedicationReminder(data.item.code)"></b-icon-bell-fill>
                </div>
                <div v-if="data.item.recurrenceReminderId" class="mb-2">
                    <b-icon-bell-slash-fill @click.prevent="removeReminder(data.item.id)" class="bell-slash-reminder"></b-icon-bell-slash-fill>
                </div>
                <div>
                    <base-icon class="clickable" src="/static-appopensalud/img/icons/morillas-svg-icons/primary/remove.svg" @click.prevent="remove(data)" :color="RED_COLOUR"></base-icon>
                </div>
                <hr>
            </template>
        </paginated-table>
    </base-card>
    <base-dialog v-model="showReminderCreateDialog" :content="$t('wantAddReminderMed')" :okBtnText="$t('yes')" :cancelBtnText="$t('no')" :okFn="() => createMedicationReminder()" :cancelFn="() => showReminderCreateDialog = false"></base-dialog>
</div>
</template>

<script>
import PaginatedTable from '@/components/base/PaginatedTable'
import BaseDialog from '@/components/base/BaseDialog'
import ClinicHistoryRepository from '@/repositories/ClinicHistoryRepository'
import BaseSelect from '@/components/base/forms/BaseSelect'
import BaseButton from '@/components/base/buttons/BaseButton'
import AddButton from '@/components/base/buttons/AddButton'
import filters from '@/plugins/filters'
import { GREEN_COLOUR, RED_COLOUR } from '@/constants/colours'
import BaseDatepicker from '../base/forms/BaseDatepicker.vue'
import BaseCheckbox from '../base/forms/BaseCheckbox.vue'
import clinicHistoryMixin from '@/mixins/clinicHistory.mixin'

const MEDICATION_REMINDER_PATH = '/patient/clinic-history/medication-reminder'

export default {
    name: 'history-medicines-section',
    components: {
        PaginatedTable,
        BaseSelect,
        BaseButton,
        AddButton,
        BaseDatepicker,
        BaseCheckbox,
        BaseDialog
    },
    props: {
        patientId: {
            type: String,
            required: false,
            default: null
        },
        past: Boolean
    },
    mixins: [clinicHistoryMixin],
    computed: {
        stacked () {
            return window.innerWidth < 810
        },
        clinicHistoryRepository () {
            return ClinicHistoryRepository()
        }
    },
    data () {
        return {
            medicines: [],
            showAddSection: false,
            currentlyTakeMed: false,
            tableKey: 1,
            medicationReminder: {
                show: false,
                id: null,
                code: null,
                created: false
            },
            showReminderCreateDialog: false,
            table: {
                fields: [
                    {
                        label: this.$t('description-column'),
                        key: this.$i18nHelpers.normalizeProp('entity.name')
                    },
                    {
                        label: this.$t('dateStart'),
                        key: 'dateStart',
                        formatter: (value, key, item) => {
                            return value ? filters.beautifySimpleDate(value) : '-'
                        },
                        sortable: true
                    },
                    {
                        label: this.$t('dateEnd'),
                        key: 'dateEnd',
                        formatter: (value, key, item) => {
                            return value ? filters.beautifySimpleDate(value) : '-'
                        },
                        sortable: true
                    },
                    {
                        label: '',
                        key: 'actionsMobile',
                        class: 'table-actions'
                    },
                    {
                        label: '',
                        key: 'currentlyNoTakeMed',
                        hiddenStacked: true
                    },
                    {
                        label: '',
                        key: 'reminder',
                        hiddenStacked: true
                    },
                    {
                        label: '',
                        key: 'removeReminder',
                        hiddenStacked: true
                    },
                    {
                        label: '',
                        key: 'remove',
                        hiddenStacked: true
                    }
                ],
                filters: {
                    specialtyId: null
                }
            },
            loading: false,
            select: {
                options: [],
                value: null,
                loading: false
            },
            medicationDate: null,
            medicationEndDate: null,
            RED_COLOUR,
            GREEN_COLOUR
        }
    },
    methods: {
        stopMedication (data) {
            this.clinicHistoryRepository.stopHistoryMedication({
                medicationId: data.item.id
            }).then(response => {
                if (response.data.data) {
                    this.load()
                }
            })
        },
        showNoTakeMedSelector (dateEnd) {
            return !dateEnd || new Date(dateEnd) > new Date()
        },
        load () {
            this.loading = true
            this.clinicHistoryRepository.getHistoryMedications().then((response) => {
                let data = response.data.data
                this.medicines = this.past ? data.filter(item => item.dateEnd && new Date(item.dateEnd) < new Date()) : data.filter(item => !item.dateEnd || new Date(item.dateEnd) >= new Date())
                this.loading = false
                this.tableKey++
            })
        },
        loadSelectOptions (search) {
            if (search) {
                this.select.loading = true
                this.clinicHistoryRepository.getMedicinesByName(search).then((response) => {
                    this.select.options = response.data.data
                    this.select.loading = false
                })
            }
        },
        save () {
            if (!this.select.value) {
                this.$store.dispatch('showInfoAlert', this.$t('medicationSelectError'))
                return
            }
            if (this.medicationDate && this.medicationEndDate && new Date(this.medicationEndDate) < new Date(this.medicationDate)) {
                this.$store.dispatch('showInfoAlert', this.$t('medicationEndDateMinorStartDate'))
                return
            }
            this.clinicHistoryRepository.saveHistoryMedication({
                medicationId: this.select.value,
                dateStart: this.medicationDate,
                dateEnd: this.medicationEndDate
            }).then(response => {
                let data = response.data
                if (data && data.status === 200) {
                    this.medicines.unshift(data.data)
                    this.medicationReminder.id = data.data.id
                    this.medicationReminder.code = this.select.value
                    this.showReminderCreateDialog = true
                }
                this.medicationDate = null
                this.medicationEndDate = null
                this.select.value = null
                this.select.options = []
            })
        },
        createMedicationReminder (code) {
            this.$router.push(`${MEDICATION_REMINDER_PATH}/${code || this.medicationReminder.code}`)
        },
        removeReminder (id) {
            ClinicHistoryRepository().deleteMedicationReminder(id).then(response => {
                let data = response.data.data
                if (data) {
                        this.load()
                    } else {
                        this.$store.dispatch('showDangerAlert', this.$t('error.try-again'))
                    }
            })
        },
        remove (data) {
            let id = data.item.id
            this.clinicHistoryRepository.deleteHistoryMedication(id).then(response => {
                if (response.data.data) {
                    this.load()
                }
            })
        },
        setShowAddSection () {
            this.showAddSection = true
        }
    },
    created () {
        this.load()
    }

}
</script>

<style lang="scss" scoped>
.checkbox-med{
    text-align: center;
    margin-top: auto;
    margin-bottom: auto;
}
.bell-reminder{
    cursor: pointer;
    color: $green;
}
.bell-slash-reminder{
    cursor: pointer;
    color: $red;
}
.blue{
    color: $blue
}
</style>>


// WEBPACK FOOTER //
// src/components/shared/HistoryMedicinesSection.vue